import { useState, useEffect } from "react";
import { createClient } from "@supabase/supabase-js";

const SUPABASE_URL  = "https://hqarozilsolrobyufwrp.supabase.co";
const SUPABASE_ANON = "sb_publishable_STZ3J7jxJ4AFXEgWjrFQZw_WnLQxAWz";

const supabase = createClient(SUPABASE_URL, SUPABASE_ANON);

const s = {
  page:    { minHeight:"100vh", background:"#0f0e17", color:"#fffffe", fontFamily:"'Inter',sans-serif", padding:"2rem" },
  title:   { fontSize:"1.6rem", fontWeight:700, color:"#a89af8", marginBottom:"0.3rem" },
  sub:     { color:"#a7a3b8", fontSize:"0.85rem", marginBottom:"2rem" },
  grid:    { display:"grid", gridTemplateColumns:"1fr 1fr", gap:"1.5rem", maxWidth:"1100px" },
  card:    { background:"#1a1826", border:"1px solid #2e2b3e", borderRadius:"14px", padding:"1.5rem" },
  label:   { display:"block", fontSize:"0.72rem", color:"#a7a3b8", textTransform:"uppercase", letterSpacing:"1.2px", marginBottom:"6px", fontFamily:"monospace" },
  input:   { width:"100%", background:"#16141f", border:"1px solid #2e2b3e", borderRadius:"8px", color:"#fffffe", padding:"0.6rem 0.9rem", fontSize:"0.9rem", fontFamily:"'Inter',sans-serif", outline:"none", boxSizing:"border-box" },
  textarea:{ width:"100%", background:"#16141f", border:"1px solid #2e2b3e", borderRadius:"8px", color:"#fffffe", padding:"0.6rem 0.9rem", fontSize:"0.85rem", fontFamily:"monospace", outline:"none", resize:"vertical", boxSizing:"border-box", lineHeight:1.6 },
  btn:     { background:"#7c6af5", color:"#fff", border:"none", borderRadius:"8px", padding:"0.6rem 1.4rem", cursor:"pointer", fontSize:"0.88rem", fontWeight:600 },
  btnGreen:{ background:"#1a6b3c", color:"#fff", border:"none", borderRadius:"8px", padding:"0.6rem 1.4rem", cursor:"pointer", fontSize:"0.88rem", fontWeight:600 },
  btnDanger:{ background:"#c0392b", color:"#fff", border:"none", borderRadius:"8px", padding:"0.5rem 1rem", cursor:"pointer", fontSize:"0.82rem" },
  btnOutline:{ background:"transparent", color:"#7c6af5", border:"1px solid #7c6af5", borderRadius:"8px", padding:"0.5rem 1rem", cursor:"pointer", fontSize:"0.82rem" },
  sectionTitle:{ fontSize:"1rem", fontWeight:600, color:"#fffffe", marginBottom:"1rem" },
  row:     { display:"flex", gap:"0.75rem", alignItems:"center", flexWrap:"wrap" },
  badge:   (active) => ({ background:active?"#1a3a1a":"#2a1a1a", color:active?"#6ef078":"#ff7f7f", border:`1px solid ${active?"#2e6e2e":"#6e2e2e"}`, borderRadius:"20px", padding:"2px 10px", fontSize:"0.75rem", fontFamily:"monospace" }),
  toast:   (type) => ({ position:"fixed", bottom:"2rem", right:"2rem", background:type==="success"?"#1a3a1a":"#3a1a1a", color:type==="success"?"#6ef078":"#ff7f7f", border:`1px solid ${type==="success"?"#3e7e3e":"#7e3e3e"}`, borderRadius:"10px", padding:"0.75rem 1.2rem", fontSize:"0.88rem", zIndex:999 }),
  charCount:{ fontSize:"0.75rem", color:"#a7a3b8", fontFamily:"monospace", marginTop:"4px" },
  th:      { background:"#211f2e", color:"#a7a3b8", padding:"0.6rem 0.8rem", fontSize:"0.75rem", textTransform:"uppercase", letterSpacing:"1px", fontFamily:"monospace", textAlign:"left", borderBottom:"1px solid #2e2b3e" },
  td:      { padding:"0.7rem 0.8rem", fontSize:"0.82rem", borderBottom:"1px solid #1e1c2b", color:"#fffffe", verticalAlign:"top" },
  pill:    (color) => ({ background:color+"22", color:color, border:`1px solid ${color}44`, borderRadius:"12px", padding:"2px 8px", fontSize:"0.72rem", fontFamily:"monospace", whiteSpace:"nowrap" }),
};

export default function AdminPanel() {
  const [contents,  setContents]  = useState([]);
  const [leads,     setLeads]     = useState([]);
  const [settings,  setSettings]  = useState({ bot_name:"", welcome_message:"", primary_color:"#1a6b3c", placeholder:"" });
  const [newDoc,    setNewDoc]    = useState({ title:"", content:"", description:"" });
  const [loading,   setLoading]   = useState(false);
  const [toast,     setToast]     = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editDoc,   setEditDoc]   = useState({});
  const [activeTab, setActiveTab] = useState("content");

  const showToast = (msg, type="success") => { setToast({msg,type}); setTimeout(()=>setToast(null),3000); };

  const load = async () => {
    const [c, s, l] = await Promise.all([
      supabase.from("chatbot_content").select("*").order("created_at",{ascending:false}),
      supabase.from("chatbot_settings").select("*").limit(1).single(),
      supabase.from("leads").select("*").order("created_at",{ascending:false}),
    ]);
    if (c.data) setContents(c.data);
    if (s.data) setSettings(s.data);
    if (l.data) setLeads(l.data);
  };

  useEffect(()=>{ load(); },[]);

  const setActive = async (id) => {
    setLoading(true);
    await supabase.from("chatbot_content").update({is_active:false}).neq("id",id);
    await supabase.from("chatbot_content").update({is_active:true}).eq("id",id);
    showToast("Active document updated!");
    load(); setLoading(false);
  };

  const deleteDoc = async (id) => {
    if (!window.confirm("Delete this document?")) return;
    await supabase.from("chatbot_content").delete().eq("id",id);
    showToast("Deleted.","error"); load();
  };

  const deleteLead = async (id) => {
    if (!window.confirm("Delete this lead?")) return;
    await supabase.from("leads").delete().eq("id",id);
    showToast("Lead deleted.","error"); load();
  };

  const addDoc = async () => {
    if (!newDoc.title.trim()||!newDoc.content.trim()) return showToast("Title and content required","error");
    setLoading(true);
    const {error} = await supabase.from("chatbot_content").insert([{...newDoc, is_active:false}]);
    if (error) return showToast("Error: "+error.message,"error");
    showToast("Document added!");
    setNewDoc({title:"",content:"",description:""});
    load(); setLoading(false);
  };

  const saveEdit = async () => {
    setLoading(true);
    await supabase.from("chatbot_content").update({title:editDoc.title,content:editDoc.content,description:editDoc.description,updated_at:new Date().toISOString()}).eq("id",editingId);
    showToast("Document updated!"); setEditingId(null); load(); setLoading(false);
  };

  const saveSettings = async () => {
    setLoading(true);
    await supabase.from("chatbot_settings").update({bot_name:settings.bot_name,welcome_message:settings.welcome_message,primary_color:settings.primary_color,placeholder:settings.placeholder,updated_at:new Date().toISOString()}).eq("id",settings.id);
    showToast("Settings saved!"); setLoading(false);
  };

  // ── UPDATED: exportToExcel with new columns ──
  const exportToExcel = () => {
    const headers = ["ID","Name","Phone","Email","Passengers","Kids/Infants","Travel Dates","Flexible","Departure","Stay","Property","Passport","Date"];
    const rows = leads.map(l=>[
      l.id, l.name, l.phone, l.email, l.passengers, l.kids_infants,
      l.travel_dates, l.flexible_dates, l.departure_airport,
      l.stay_duration, l.property_type, l.passport_type,
      new Date(l.created_at).toLocaleString()
    ]);
    const csv = [headers,...rows].map(r=>r.map(v=>`"${(v||"").toString().replace(/"/g,'""')}"`).join(",")).join("\n");
    const blob = new Blob([csv],{type:"text/csv"});
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a");
    a.href=url; a.download=`haq_travels_leads_${new Date().toISOString().slice(0,10)}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const tabStyle = (tab) => ({
    padding:"0.6rem 1.2rem", cursor:"pointer", border:"none", borderRadius:"8px",
    background: activeTab===tab ? "#7c6af5" : "transparent",
    color: activeTab===tab ? "#fff" : "#a7a3b8",
    fontSize:"0.88rem", fontWeight: activeTab===tab ? 600 : 400,
    transition:"all 0.2s",
  });

  return (
    <div style={s.page}>
      <div style={s.title}>Chatbot Admin Panel</div>
      <div style={s.sub}>Manage content, view leads, update settings — no code needed</div>

      {/* Tabs */}
      <div style={{display:"flex",gap:"0.5rem",marginBottom:"1.5rem",background:"#1a1826",borderRadius:"10px",padding:"0.4rem",maxWidth:"500px"}}>
        <button style={tabStyle("content")} onClick={()=>setActiveTab("content")}>📄 Content</button>
        <button style={tabStyle("leads")} onClick={()=>setActiveTab("leads")}>
          👥 Leads {leads.length > 0 && <span style={{background:"#7c6af5",color:"#fff",borderRadius:"10px",padding:"1px 7px",fontSize:"0.72rem",marginLeft:"4px"}}>{leads.length}</span>}
        </button>
        <button style={tabStyle("settings")} onClick={()=>setActiveTab("settings")}>⚙️ Settings</button>
      </div>

      {/* CONTENT TAB */}
      {activeTab === "content" && (
        <div style={s.grid}>
          <div style={{display:"flex",flexDirection:"column",gap:"1.2rem"}}>
            <div style={s.card}>
              <div style={s.sectionTitle}>Add New Document</div>
              <label style={s.label}>Title</label>
              <input style={{...s.input,marginBottom:"1rem"}} placeholder="e.g. Umrah Packages 2025" value={newDoc.title} onChange={e=>setNewDoc(p=>({...p,title:e.target.value}))} />
              <label style={s.label}>Short Description</label>
              <input style={{...s.input,marginBottom:"1rem"}} placeholder="Optional description" value={newDoc.description} onChange={e=>setNewDoc(p=>({...p,description:e.target.value}))} />
              <label style={s.label}>Content (paste your package info, FAQs, hotel details etc.)</label>
              <textarea style={{...s.textarea,minHeight:"180px",marginBottom:"4px"}} placeholder="Paste all your Hajj/Umrah package info here..." value={newDoc.content} onChange={e=>setNewDoc(p=>({...p,content:e.target.value}))} />
              <div style={s.charCount}>{newDoc.content.length.toLocaleString()} characters</div>
              <div style={{marginTop:"1rem"}}>
                <button style={s.btn} onClick={addDoc} disabled={loading}>+ Add Document</button>
              </div>
            </div>
          </div>

          <div style={s.card}>
            <div style={s.sectionTitle}>Your Documents ({contents.length})</div>
            {contents.length===0 && <div style={{color:"#a7a3b8",fontSize:"0.85rem"}}>No documents yet.</div>}
            {contents.map(doc=>(
              <div key={doc.id} style={{borderBottom:"1px solid #2e2b3e",paddingBottom:"1rem",marginBottom:"1rem"}}>
                {editingId===doc.id ? (
                  <div>
                    <input style={{...s.input,marginBottom:"0.6rem"}} value={editDoc.title} onChange={e=>setEditDoc(p=>({...p,title:e.target.value}))} />
                    <input style={{...s.input,marginBottom:"0.6rem"}} value={editDoc.description} onChange={e=>setEditDoc(p=>({...p,description:e.target.value}))} />
                    <textarea style={{...s.textarea,minHeight:"150px",marginBottom:"0.6rem"}} value={editDoc.content} onChange={e=>setEditDoc(p=>({...p,content:e.target.value}))} />
                    <div style={s.charCount}>{(editDoc.content||"").length.toLocaleString()} characters</div>
                    <div style={{...s.row,marginTop:"0.75rem"}}>
                      <button style={s.btn} onClick={saveEdit} disabled={loading}>Save</button>
                      <button style={s.btnOutline} onClick={()=>setEditingId(null)}>Cancel</button>
                    </div>
                  </div>
                ):(
                  <div>
                    <div style={{...s.row,marginBottom:"6px"}}>
                      <span style={{fontWeight:600,fontSize:"0.95rem"}}>{doc.title}</span>
                      <span style={s.badge(doc.is_active)}>{doc.is_active?"● Active":"○ Inactive"}</span>
                    </div>
                    {doc.description && <div style={{color:"#a7a3b8",fontSize:"0.8rem",marginBottom:"6px"}}>{doc.description}</div>}
                    <div style={{color:"#a7a3b8",fontSize:"0.75rem",fontFamily:"monospace",marginBottom:"10px"}}>{doc.content.length.toLocaleString()} chars</div>
                    <div style={s.row}>
                      {!doc.is_active && <button style={s.btn} onClick={()=>setActive(doc.id)}>Set Active</button>}
                      <button style={s.btnOutline} onClick={()=>{setEditingId(doc.id);setEditDoc({title:doc.title,content:doc.content,description:doc.description||""});}}>Edit</button>
                      <button style={s.btnDanger} onClick={()=>deleteDoc(doc.id)}>Delete</button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* LEADS TAB */}
      {activeTab === "leads" && (
        <div style={s.card}>
          <div style={{...s.row,marginBottom:"1.2rem"}}>
            <div style={s.sectionTitle}>Collected Leads ({leads.length})</div>
            {leads.length > 0 && (
              <button style={s.btnGreen} onClick={exportToExcel}>⬇ Export to Excel</button>
            )}
          </div>

          {leads.length === 0 ? (
            <div style={{color:"#a7a3b8",fontSize:"0.9rem",textAlign:"center",padding:"2rem"}}>
              <div style={{fontSize:"2rem",marginBottom:"0.5rem"}}>👥</div>
              No leads yet. They will appear here when visitors chat with your bot.
            </div>
          ) : (
            <div style={{overflowX:"auto"}}>
              <table style={{width:"100%",borderCollapse:"collapse",minWidth:"1100px"}}>
                <thead>
                  {/* ── UPDATED: new column headers ── */}
                  <tr>
                    {["Name","Phone","Email","Passengers","Kids/Infants","Travel Dates","Dates Flexible","Departure","Stay","Property","Passport","Date",""].map(h=>(
                      <th key={h} style={s.th}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {leads.map(lead=>(
                    // ── UPDATED: new column data ──
                    <tr key={lead.id}
                      onMouseEnter={e=>e.currentTarget.style.background="#211f2e"}
                      onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                      <td style={s.td}><strong>{lead.name||"—"}</strong></td>
                      <td style={s.td}><span style={s.pill("#6ef078")}>{lead.phone||"—"}</span></td>
                      <td style={s.td}>{lead.email||"—"}</td>
                      <td style={{...s.td,textAlign:"center"}}>{lead.passengers||"—"}</td>
                      <td style={s.td}>{lead.kids_infants||"None"}</td>
                      <td style={s.td}>{lead.travel_dates||"—"}</td>
                      <td style={{...s.td,textAlign:"center"}}>{lead.flexible_dates||"—"}</td>
                      <td style={s.td}>{lead.departure_airport||"—"}</td>
                      <td style={s.td}>{lead.stay_duration||"—"}</td>
                      <td style={s.td}><span style={s.pill("#f5c542")}>{lead.property_type||"—"}</span></td>
                      <td style={s.td}><span style={s.pill("#a89af8")}>{lead.passport_type||"—"}</span></td>
                      <td style={{...s.td,fontSize:"0.72rem",color:"#a7a3b8",whiteSpace:"nowrap"}}>{new Date(lead.created_at).toLocaleString()}</td>
                      <td style={s.td}><button style={s.btnDanger} onClick={()=>deleteLead(lead.id)}>✕</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* SETTINGS TAB */}
      {activeTab === "settings" && (
        <div style={{maxWidth:"500px"}}>
          <div style={s.card}>
            <div style={s.sectionTitle}>Chatbot Settings</div>
            <label style={s.label}>Bot Name</label>
            <input style={{...s.input,marginBottom:"1rem"}} value={settings.bot_name||""} onChange={e=>setSettings(p=>({...p,bot_name:e.target.value}))} />
            <label style={s.label}>Welcome Message</label>
            <textarea style={{...s.textarea,minHeight:"80px",marginBottom:"1rem"}} value={settings.welcome_message||""} onChange={e=>setSettings(p=>({...p,welcome_message:e.target.value}))} />
            <label style={s.label}>Input Placeholder</label>
            <input style={{...s.input,marginBottom:"1rem"}} value={settings.placeholder||""} onChange={e=>setSettings(p=>({...p,placeholder:e.target.value}))} />
            <label style={s.label}>Brand Color</label>
            <div style={{...s.row,marginBottom:"1.5rem"}}>
              <input type="color" value={settings.primary_color||"#1a6b3c"} onChange={e=>setSettings(p=>({...p,primary_color:e.target.value}))} style={{width:"44px",height:"36px",border:"none",background:"none",cursor:"pointer"}} />
              <input style={{...s.input,flex:1}} value={settings.primary_color||""} onChange={e=>setSettings(p=>({...p,primary_color:e.target.value}))} />
            </div>
            <button style={s.btn} onClick={saveSettings} disabled={loading}>Save Settings</button>
          </div>
        </div>
      )}

      {toast && <div style={s.toast(toast.type)}>{toast.msg}</div>}
    </div>
  );
}